import produce from 'immer';
import {compact, each, findIndex, keyBy, toNumber} from 'lodash';
import {packId} from 'common/diplomatIdUtil';
import {DiplomatFolder, IDiplomatFolder} from 'types/DiplomatFolder';
import {IDiplomatObject} from 'types/DiplomatObject';
import {ensureFirstLevelOrder} from './ensureFirstLevelOrder';
import {DiplomatObjectType} from 'types/DiplomatObjectType';
import {ALL_USERS, SFTP_GROUPS} from 'common/constants';
import {alphaNumericSortBy, baseCollator, baseNameComparator} from 'common/stringUtils';

export type INav = {
   // NOTE: id combined objectType/Id for all types except folders
   child?: INav[];
   count?: number;
} & IDiplomatFolder;

export type INavState = {
   _state: {
      depth: number;
      expanded?: boolean;
   };
} & INav;

class FoldersBuilder {
   private mapById: any = {};
   private foldersMap: {[id: string]: IDiplomatFolder};

   constructor(folders: IDiplomatFolder[]) {
      this.foldersMap = keyBy(folders, 'id');
   }

   private pushToParent = (item: INav) => {
      const parentFolder = this.mapById[item.folderId as string];
      if (!parentFolder) {
         // TODO host key is receiving without folderId...
         console.warn('Parent folder is not found for item', item.id);
         return false;
      } else {
         parentFolder.child = parentFolder.child || [];
         parentFolder.child.push(item);
         return true;
      }
   };

   private populateBy = (item?: any) => {
      if (typeof item?.folderId === 'string') {
         const _item = produce(item as INav, (draft) => {
            // draft.imageUrl = iconUrl;
            draft._id = draft.id;
            draft.id = packId(draft.objectType, draft.id);
         });

         this.pushToParent(_item);
         this.mapById[_item.id] = item;
      } else {
         each(item, (i) => this.populateBy(i));
      }
   };

   private countChildren = (data: INav): number => {
      if (data.child && data.child.length) {
         const count = data.child
            .map((item: any) => this.countChildren(item))
            .reduce((total: number, itemCount: number) => total + itemCount, 0);

         data.count = count;

         return count;
      } else if (!data.level) {
         return 1;
      }
      data.count = 0;
      return 0;
   };

   fillParent = (idsMap: Set<IDiplomatFolder>, folderId: string) => {
      const folder = this.foldersMap[folderId];
      if (folder) {
         idsMap.add(this.foldersMap[folderId]);
         this.fillParent(idsMap, this.foldersMap[folderId].folderId);
      } else {
         console.debug('Folder not found for folderId:', folderId);
      }
   };

   buildFilteredFoldersWithObjects = (objects: IDiplomatObject[]) => {
      const foldersMap = new Set<IDiplomatFolder>(); // [] as string[];
      objects?.forEach((o) => this.fillParent(foldersMap, o.folderId));

      const folders = Array.from(foldersMap);
      const _sorted = alphaNumericSortBy(folders, ['level', 'name']);

      // console.debug(_sorted);
      const sortedFolders = produce(_sorted as (IDiplomatFolder | null)[], (sorted) => {
         this.mapById = keyBy(sorted, 'id');

         // iterate other each folder and fill with nested folders
         sorted.forEach((folder: INav | null, i) => {
            if (this.pushToParent(folder!)) {
               sorted[i] = null;
            }
         });

         // fill with items inside main folders
         this.populateBy(alphaNumericSortBy(objects!, ['name']));
      });

      // const root = find(sortedFolders, (folder: INav) => toNumber(folder.level) === 0);

      return compact(sortedFolders);
   };
}

export default FoldersBuilder;
